import { EstimateRemark } from "@/js/interfaces/DTOs/Models";

export class EstimateRemarkBag {
    private readonly remarks: Map<number, EstimateRemark> = new Map();
    private _cache: Array<EstimateRemark> | null = null;

    constructor(estimateRemarks: Array<EstimateRemark> = []) {
        for (const remark of estimateRemarks) {
            this.remarks.set(remark.id, remark);
        }
    }

    /**
     * Get all estimate remarks in the bag.
     */
    public all(): Array<EstimateRemark> {
        if (null === this._cache) {
            this._cache = Array.from(this.remarks.values());
        }
        return this._cache;
    }

    /**
     * Add an estimate remark to the bag.
     *
     * @param newRemark The estimate remark to add
     */
    public add(newRemark: EstimateRemark): void {
        if (this.has(newRemark.id)) {
            throw new Error(`Trying to add an estimate remark with id ${newRemark.id} that is already in the bag`);
        }
        this.remarks.set(newRemark.id, newRemark);
        this._cache = null;
    }

    /**
     * Delete an estimate remark from the bag with a given remark id.
     *
     * @param remarkId The remark id to find where the remark is stored
     */
    public delete(remarkId: number): void {
        if (!this.remarks.delete(remarkId)) {
            throw new Error(`Trying to delete an estimate remark with id ${remarkId} that is not in the bag`);
        }
        this._cache = null;
    }

    /**
     * Check if there is an estimate remark for a given remark id
     */
    public has(remarkId: number): boolean {
        return this.remarks.has(remarkId);
    }

    /**
     * Retrieve an estimate remark for a given remark id.
     *
     * @param remarkId The remark id to find where the remark is stored
     */
    public get(remarkId: number): EstimateRemark {
        const item = this.remarks.get(remarkId);
        if (undefined === item) {
            throw new Error(`Trying to get an estimate remark with id ${remarkId} that is not in the bag`);
        }
        return item;
    }

    /**
     * Retrieve the estimate remarks associated with an estimate.
     */
    public count(): number {
        return this.remarks.size;
    }

    /**
     * Check if there is any estimate remark in the bag
     */
    public isEmpty(): boolean {
        return 0 === this.remarks.size;
    }
}